import { Badge } from '../../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';

export interface ComplianceRecord {
  id: string;
  type: string;
  status: 'compliant' | 'pending' | 'non-compliant';
  lastChecked: string;
}

interface ComplianceTrackingProps {
  records: ComplianceRecord[];
}

const ComplianceTracking = ({ records }: ComplianceTrackingProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return (
          <Badge className="bg-green-500 hover:bg-green-600">Compliant</Badge>
        );
      case 'non-compliant':
        return <Badge variant="destructive">Non-compliant</Badge>;
      case 'pending':
        return (
          <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'non-compliant':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Compliance Tracking</CardTitle>
        <CardDescription>
          Monitor your compliance status and screening results
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {records.map(record => (
            <div
              key={record.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {getStatusIcon(record.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{record.type}</div>
                  <div className="text-sm text-muted-foreground">
                    Last checked: {record.lastChecked}
                  </div>
                </div>
                {getStatusBadge(record.status)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplianceTracking;
